import { useState } from "react";
import Modal from "../../layouts/Modal";
import {
  EngineCondition,
  BodyCondition,
  PricingContext,
  ISpecificRuleTestRequest,
  ISpecificRuleTestResponse,
  ISpecificRuleTestResult,
} from "../../models/PriceSettings";
import useApiHelper from "../../hooks/useApiHelper";
import PriceFormat from "../../utils/priceFormat";
import {
  CheckCircleIcon,
  XCircleIcon,
  ExclamationTriangleIcon,
  BeakerIcon,
  TruckIcon,
} from "@heroicons/react/24/outline";

interface SpecificRuleTestModalProps {
  open: boolean;
  setOpen: React.Dispatch<React.SetStateAction<boolean>>;
}

const engineConditionOptions = [
  { id: EngineCondition.NoFault, label: "Good (No Fault)" },
  { id: EngineCondition.MinorFault, label: "Average (Minor Fault)" },
  { id: EngineCondition.MajorFault, label: "Bad (Major Fault)" },
];

const bodyConditionOptions = [
  { id: BodyCondition.Good, label: "Good" },
  { id: BodyCondition.Average, label: "Average" },
  { id: BodyCondition.HeavyDamage, label: "Bad (Heavy Damage)" },
];

const contextOptions = [
  { id: PricingContext.Scrap, label: "Scrap" },
  { id: PricingContext.Salvage, label: "Salvage" },
  { id: PricingContext.Smart, label: "Smart" },
];

// The values every rule is matched against. Worth showing verbatim — vehicle data arrives with its
// own spacing ("M 111.955"), and a blank here is why an otherwise correct rule never fires.
function VehicleField({ label, value }: { label: string; value: string | number | null }) {
  const missing = value === null || value === "" || value === 0;
  return (
    <div className="flex items-baseline justify-between gap-2 py-1">
      <span className="text-xs text-gray-500">{label}</span>
      <span className={`text-xs font-mono ${missing ? "text-amber-600 italic" : "text-gray-800"}`}>
        {missing ? "not set" : value}
      </span>
    </div>
  );
}

function RuleCard({
  rule,
  matched,
  winsOutright,
  exclusiveWinner,
}: {
  rule: ISpecificRuleTestResult;
  matched: boolean;
  winsOutright?: boolean;
  exclusiveWinner?: boolean;
}) {
  const price =
    rule.price === null
      ? null
      : rule.isFixedPrice
      ? PriceFormat(rule.price)
      : `${rule.price}%`;

  return (
    <div
      className={`p-3 rounded-lg border ${
        matched ? "bg-green-50 border-green-200" : "bg-gray-50 border-gray-200"
      }`}
    >
      <div className="flex items-start justify-between gap-3">
        <div className="min-w-0">
          <div className="flex items-center gap-2 flex-wrap">
            {matched ? (
              <CheckCircleIcon className="h-4 w-4 text-green-500 flex-shrink-0" />
            ) : (
              <XCircleIcon className="h-4 w-4 text-gray-400 flex-shrink-0" />
            )}
            <span className="text-sm font-semibold text-gray-800">Rule {rule.ruleId ?? "-"}</span>
            {!rule.isActive && (
              <span className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-gray-200 text-gray-600">
                Inactive
              </span>
            )}
            {rule.isStaticOverride && (
              <span
                className={`inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium ${
                  winsOutright ? "bg-blue-100 text-blue-800" : "bg-gray-200 text-gray-600"
                }`}
                title="Fixed prices replace the whole quote instead of adding to it, before scrap, salvage or smart are priced. They need Applies To Scrap."
              >
                {winsOutright ? "Sets the whole price" : "Fixed price"}
              </span>
            )}
            {rule.isExclusive && (
              <span
                className={`inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium ${
                  exclusiveWinner ? "bg-purple-100 text-purple-800" : "bg-gray-200 text-gray-600"
                }`}
                title="When an exclusive rule matches, only its top up is added."
              >
                {exclusiveWinner ? "Only top up added" : "Exclusive"}
              </span>
            )}
            {rule.capsOnly && (
              <span
                className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-amber-100 text-amber-800"
                title="This rule has no top up, so all it does is hold the quote down to its max price."
              >
                Cap only
              </span>
            )}
          </div>
          <p className="text-xs text-gray-500 mt-1 break-words">
            {rule.conditions.length > 0 ? rule.conditions.join(", ") : "Matches any vehicle"}
          </p>
        </div>
        {/* The cap is the whole point of a cap-only rule, so it takes the price slot rather than
            leaving the card looking like it does nothing. */}
        <div className="flex-shrink-0 text-right">
          {price && (
            <span className={`text-sm font-semibold ${matched ? "text-green-700" : "text-gray-500"}`}>
              {price}
            </span>
          )}
          {rule.maximumPrice !== null && (
            <p className={`text-xs ${matched ? "text-amber-700" : "text-gray-500"} ${price ? "mt-0.5" : ""}`}>
              max {PriceFormat(rule.maximumPrice)}
            </p>
          )}
        </div>
      </div>

      {rule.notes.length > 0 && (
        <ul className="mt-2 pt-2 border-t border-gray-200 space-y-1">
          {rule.notes.map((note, i) => (
            <li key={i} className="text-xs text-amber-700 flex items-start gap-1.5">
              <span className="text-amber-400 mt-0.5">&bull;</span>
              <span className="break-words">{note}</span>
            </li>
          ))}
        </ul>
      )}

      {rule.reasons.length > 0 && (
        <ul className="mt-2 pt-2 border-t border-gray-200 space-y-1">
          {rule.reasons.map((reason, i) => (
            <li key={i} className="text-xs text-red-700 flex items-start gap-1.5">
              <span className="text-red-400 mt-0.5">&bull;</span>
              <span className="break-words">{reason}</span>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}

export default function SpecificRuleTestModal({ open, setOpen }: SpecificRuleTestModalProps) {
  const { post } = useApiHelper();

  const [vrm, setVrm] = useState("");
  const [engineCondition, setEngineCondition] = useState<EngineCondition>(EngineCondition.NoFault);
  const [bodyCondition, setBodyCondition] = useState<BodyCondition>(BodyCondition.Good);
  const [context, setContext] = useState<PricingContext>(PricingContext.Scrap);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [result, setResult] = useState<ISpecificRuleTestResponse | null>(null);
  const [showAllUnmatched, setShowAllUnmatched] = useState(false);

  const handleClose = () => {
    setOpen(false);
    setResult(null);
    setError(null);
    setVrm("");
    setShowAllUnmatched(false);
    setEngineCondition(EngineCondition.NoFault);
    setBodyCondition(BodyCondition.Good);
    setContext(PricingContext.Scrap);
  };

  const handleTest = async () => {
    const trimmed = vrm.trim().toUpperCase();
    if (!trimmed) {
      setError("Please enter a VRM.");
      return;
    }
    setError(null);
    setResult(null);
    setShowAllUnmatched(false);
    setIsLoading(true);
    try {
      const response = await post<ISpecificRuleTestRequest, { success: boolean; data: ISpecificRuleTestResponse }>(
        "/priceSetting/generic/test",
        { vrm: trimmed, engineCondition, bodyCondition, context }
      );
      if (response?.data) {
        setResult(response.data);
      } else {
        setError("Unexpected response from server.");
      }
    } catch (err: any) {
      setError(err?.message ?? "An error occurred while testing the rules.");
    } finally {
      setIsLoading(false);
    }
  };

  // Rules blocked by a single condition are the ones someone meant to match, so they lead and the
  // rest stay behind a toggle — a configuration can hold hundreds written for other vehicles.
  const nearMisses = result?.unmatched.filter((x) => x.reasons.length === 1) ?? [];
  const others = result?.unmatched.filter((x) => x.reasons.length > 1) ?? [];
  const visibleUnmatched = showAllUnmatched ? [...nearMisses, ...others] : nearMisses;

  return (
    <Modal width="max-w-2xl" open={open} setOpen={handleClose}>
      {/* Header */}
      <div className="flex items-center gap-3 mb-1">
        <div className="flex-shrink-0 p-2 rounded-lg bg-primary-50">
          <BeakerIcon className="h-5 w-5 text-primary-600" />
        </div>
        <div>
          <h2 className="text-lg font-semibold text-gray-900">Check Rules Against a Reg</h2>
          <p className="text-sm text-gray-500">
            See which specific rules match a vehicle, and why the others don't.
          </p>
        </div>
      </div>

      <div className="mt-4 border-t border-gray-200 pt-4 space-y-4">
        {/* VRM Input */}
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">VRM</label>
          <input
            type="text"
            value={vrm}
            onChange={(e) => setVrm(e.target.value.toUpperCase())}
            onKeyDown={(e) => e.key === "Enter" && handleTest()}
            placeholder="e.g. AB12 CDE"
            maxLength={10}
            className="w-full text-sm text-gray-900 border border-gray-300 rounded-md shadow-sm px-3 py-2 uppercase placeholder:normal-case focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
          />
        </div>

        {/* Conditions */}
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Engine Condition</label>
            <select
              value={engineCondition}
              onChange={(e) => setEngineCondition(Number(e.target.value) as EngineCondition)}
              className="w-full text-sm text-gray-900 border border-gray-300 rounded-md shadow-sm px-3 py-2 focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
            >
              {engineConditionOptions.map((opt) => (
                <option key={opt.id} value={opt.id}>{opt.label}</option>
              ))}
            </select>
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Body Condition</label>
            <select
              value={bodyCondition}
              onChange={(e) => setBodyCondition(Number(e.target.value) as BodyCondition)}
              className="w-full text-sm text-gray-900 border border-gray-300 rounded-md shadow-sm px-3 py-2 focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
            >
              {bodyConditionOptions.map((opt) => (
                <option key={opt.id} value={opt.id}>{opt.label}</option>
              ))}
            </select>
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Applies To</label>
            <select
              value={context}
              onChange={(e) => setContext(Number(e.target.value) as PricingContext)}
              className="w-full text-sm text-gray-900 border border-gray-300 rounded-md shadow-sm px-3 py-2 focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
            >
              {contextOptions.map((opt) => (
                <option key={opt.id} value={opt.id}>{opt.label}</option>
              ))}
            </select>
          </div>
        </div>

        {/* Error */}
        {error && (
          <div className="flex items-start gap-2 p-3 rounded-lg bg-red-50 border border-red-200">
            <XCircleIcon className="h-5 w-5 text-red-500 flex-shrink-0 mt-0.5" />
            <p className="text-sm text-red-700">{error}</p>
          </div>
        )}

        {/* Action button */}
        <button
          type="button"
          onClick={handleTest}
          disabled={isLoading}
          className="w-full rounded-md border border-transparent bg-primary-600 px-4 py-2 text-sm font-medium text-white shadow-sm hover:bg-primary-700 disabled:bg-gray-400 transition-colors"
        >
          {isLoading ? "Checking..." : "Check Rules"}
        </button>
      </div>

      {/* Results */}
      {result && (
        <div className="mt-5 border-t border-gray-200 pt-5 space-y-4">
          {!result.vehicleFound && (
            <div className="flex items-start gap-2 p-3 rounded-lg bg-amber-50 border border-amber-200">
              <ExclamationTriangleIcon className="h-5 w-5 text-amber-500 flex-shrink-0 mt-0.5" />
              <p className="text-sm text-amber-700">{result.message ?? "Vehicle not found."}</p>
            </div>
          )}

          {result.vehicleFound && (
            <>
              {/* What the rules are matched against */}
              <div className="p-3 rounded-lg bg-gray-50 border border-gray-200">
                <div className="flex items-center gap-2 mb-2">
                  <TruckIcon className="h-5 w-5 text-gray-400 flex-shrink-0" />
                  <p className="text-sm font-semibold text-gray-800">
                    {[result.vehicle.make, result.vehicle.model].filter(Boolean).join(" ") || result.vrm}
                  </p>
                </div>
                <div className="grid sm:grid-cols-2 gap-x-6">
                  <VehicleField label="Make" value={result.vehicle.make} />
                  <VehicleField label="Model" value={result.vehicle.model} />
                  <VehicleField label="Year" value={result.vehicle.year} />
                  <VehicleField label="Fuel Type" value={result.vehicle.fuelType} />
                  <VehicleField label="Engine Code" value={result.vehicle.engineCode} />
                  <VehicleField label="Engine CC" value={result.vehicle.engineCc} />
                  <VehicleField label="Transmission" value={result.vehicle.transmission} />
                </div>
              </div>

              {/* Matched */}
              <div>
                <p className="text-xs font-semibold uppercase tracking-wide text-gray-500 mb-2">
                  Matching rules ({result.matched.length})
                </p>
                {/* A fixed price isn't a top-up — the dearest matching one becomes the whole quote
                    and every rule below it is ignored, so say so rather than let the list read as a
                    running total. */}
                {result.staticOverrideId && (
                  <div className="flex items-start gap-2 p-3 mb-2 rounded-lg bg-blue-50 border border-blue-200">
                    <ExclamationTriangleIcon className="h-5 w-5 text-blue-500 flex-shrink-0 mt-0.5" />
                    <p className="text-sm text-blue-800">
                      This vehicle matches a fixed price rule, so that price is the whole quote — the
                      other matching rules below don't get added.
                    </p>
                  </div>
                )}
                {/* An exclusive rule's top up is the only one added, so the list isn't a running
                    total either. Moot under a fixed price, which already replaces everything. */}
                {result.exclusiveRuleId && !result.staticOverrideId && (
                  <div className="flex items-start gap-2 p-3 mb-2 rounded-lg bg-purple-50 border border-purple-200">
                    <ExclamationTriangleIcon className="h-5 w-5 text-purple-500 flex-shrink-0 mt-0.5" />
                    <p className="text-sm text-purple-800">
                      Rule {result.matched.find((r) => r.id === result.exclusiveRuleId)?.ruleId ?? "-"} is
                      exclusive, so its top up is the only one added — the other matching top ups below
                      don't count. Max prices still apply.
                    </p>
                  </div>
                )}
                {/* Several matched rules can each carry a cap and only the tightest binds, so name
                    the figure the quote is actually held to. */}
                {result.effectiveMaximumPrice !== null && !result.staticOverrideId && (
                  <div className="flex items-start gap-2 p-3 mb-2 rounded-lg bg-amber-50 border border-amber-200">
                    <ExclamationTriangleIcon className="h-5 w-5 text-amber-500 flex-shrink-0 mt-0.5" />
                    <p className="text-sm text-amber-800">
                      The quote is capped at {PriceFormat(result.effectiveMaximumPrice)} — the tightest
                      max price of the matching rules. Anything the rules add above that is trimmed back.
                    </p>
                  </div>
                )}
                {result.matched.length > 0 ? (
                  <div className="space-y-1.5">
                    {result.matched.map((rule) => (
                      <RuleCard
                        key={rule.id}
                        rule={rule}
                        matched
                        winsOutright={rule.id === result.staticOverrideId}
                        exclusiveWinner={rule.id === result.exclusiveRuleId}
                      />
                    ))}
                  </div>
                ) : (
                  <div className="flex items-center gap-2 p-3 rounded-lg bg-amber-50 border border-amber-200">
                    <ExclamationTriangleIcon className="h-5 w-5 text-amber-500 flex-shrink-0" />
                    <p className="text-sm text-amber-700">No specific rules match this vehicle.</p>
                  </div>
                )}
              </div>

              {/* Unmatched */}
              {result.unmatched.length > 0 && (
                <div>
                  <p className="text-xs font-semibold uppercase tracking-wide text-gray-500 mb-2">
                    {showAllUnmatched
                      ? `Rules that don't match (${result.unmatched.length})`
                      : `Closest rules that don't match (${nearMisses.length} of ${result.unmatched.length})`}
                  </p>

                  {visibleUnmatched.length > 0 ? (
                    <div className="space-y-1.5">
                      {visibleUnmatched.map((rule) => (
                        <RuleCard key={rule.id} rule={rule} matched={false} />
                      ))}
                    </div>
                  ) : (
                    <p className="text-sm text-gray-500">
                      No rule came close — every other rule missed on more than one condition.
                    </p>
                  )}

                  {others.length > 0 && (
                    <button
                      type="button"
                      onClick={() => setShowAllUnmatched((prev) => !prev)}
                      className="mt-2 text-sm font-medium text-primary-600 hover:text-primary-700"
                    >
                      {showAllUnmatched
                        ? "Show only the closest"
                        : `Show all ${result.unmatched.length} rules that don't match`}
                    </button>
                  )}
                </div>
              )}
            </>
          )}
        </div>
      )}
    </Modal>
  );
}
